import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, Search, Edit, Trash2, CheckCircle, Clock, Bell, Calendar } from 'lucide-react';
import { toast } from 'sonner';

const Anotacoes = () => {
  const [anotacoes, setAnotacoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('pendentes');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingAnotacao, setEditingAnotacao] = useState(null);
  const [formData, setFormData] = useState({
    titulo: '',
    descricao: '',
    data_alerta: '',
    hora_alerta: '',
    prioridade: 'Normal'
  });

  useEffect(() => {
    loadAnotacoes();
  }, []);

  const loadAnotacoes = async () => {
    try {
      const response = await api.getAnotacoes();
      setAnotacoes(response.data);
    } catch (error) {
      console.error('Erro ao carregar anotações:', error);
      toast.error('Erro ao carregar anotações');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const dataAlerta = new Date(`${formData.data_alerta}T${formData.hora_alerta || '09:00'}`);
      
      const submitData = {
        titulo: formData.titulo,
        descricao: formData.descricao || null,
        data_alerta: dataAlerta.toISOString(),
        prioridade: formData.prioridade
      };

      if (editingAnotacao) {
        await api.updateAnotacao(editingAnotacao.id, submitData);
        toast.success('Anotação atualizada com sucesso!');
      } else {
        await api.createAnotacao(submitData);
        toast.success('Anotação criada com sucesso!');
      }
      handleCloseDialog();
      loadAnotacoes();
    } catch (error) {
      console.error('Erro ao salvar anotação:', error);
      toast.error('Erro ao salvar anotação');
    }
  };

  const handleEdit = (anotacao) => {
    setEditingAnotacao(anotacao);
    const dataAlerta = new Date(anotacao.data_alerta);
    setFormData({
      titulo: anotacao.titulo,
      descricao: anotacao.descricao || '',
      data_alerta: dataAlerta.toISOString().split('T')[0],
      hora_alerta: dataAlerta.toTimeString().slice(0, 5),
      prioridade: anotacao.prioridade
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (anotacaoId) => {
    if (!window.confirm('Deseja realmente excluir esta anotação?')) return;
    try {
      await api.deleteAnotacao(anotacaoId);
      toast.success('Anotação excluída com sucesso!');
      loadAnotacoes();
    } catch (error) {
      console.error('Erro ao excluir anotação:', error);
      toast.error('Erro ao excluir anotação');
    }
  };

  const handleConcluir = async (anotacaoId) => {
    try {
      await api.concluirAnotacao(anotacaoId);
      toast.success('Anotação concluída!');
      loadAnotacoes();
    } catch (error) {
      console.error('Erro ao concluir anotação:', error);
      toast.error('Erro ao concluir anotação');
    }
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingAnotacao(null);
    setFormData({
      titulo: '',
      descricao: '',
      data_alerta: '',
      hora_alerta: '',
      prioridade: 'Normal'
    });
  };

  const filteredAnotacoes = anotacoes.filter(anotacao => {
    const matchesSearch = anotacao.titulo.toLowerCase().includes(searchTerm.toLowerCase()) ||
      anotacao.descricao?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'todas' ? true :
      filterStatus === 'pendentes' ? !anotacao.concluida :
      filterStatus === 'concluidas' ? anotacao.concluida : true;
    
    return matchesSearch && matchesStatus;
  });

  const getPrioridadeColor = (prioridade) => {
    const colors = {
      'Alta': 'bg-red-100 text-red-700 border-red-300',
      'Normal': 'bg-blue-100 text-blue-700 border-blue-300',
      'Baixa': 'bg-slate-100 text-slate-700 border-slate-300'
    };
    return colors[prioridade] || 'bg-slate-100 text-slate-700';
  };

  const isHoje = (dataStr) => {
    const data = new Date(dataStr);
    const hoje = new Date();
    return data.toDateString() === hoje.toDateString();
  };

  const isAtrasada = (dataStr, concluida) => {
    if (concluida) return false;
    const data = new Date(dataStr);
    return data < new Date();
  };

  if (loading) {
    return <div className="p-8" data-testid="anotacoes-loading">Carregando...</div>;
  }

  return (
    <div className="p-8 space-y-6" data-testid="anotacoes-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-semibold text-slate-900">Anotações</h1>
          <p className="text-slate-600 mt-1">Gerencie suas anotações e lembretes</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-slate-900 hover:bg-slate-800" data-testid="add-anotacao-button">
              <Plus className="w-4 h-4 mr-2" />
              Nova Anotação
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingAnotacao ? 'Editar Anotação' : 'Criar Nova Anotação'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="titulo">Título *</Label>
                <Input
                  id="titulo"
                  data-testid="anotacao-titulo-input"
                  required
                  value={formData.titulo}
                  onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="descricao">Descrição</Label>
                <textarea
                  id="descricao"
                  className="w-full min-h-[100px] p-2 border rounded-md"
                  value={formData.descricao}
                  onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                  data-testid="anotacao-descricao-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="data_alerta">Data do Alerta *</Label>
                  <Input
                    id="data_alerta"
                    type="date"
                    required
                    value={formData.data_alerta}
                    onChange={(e) => setFormData({ ...formData, data_alerta: e.target.value })}
                    data-testid="anotacao-data-input"
                  />
                </div>
                <div>
                  <Label htmlFor="hora_alerta">Hora do Alerta</Label>
                  <Input
                    id="hora_alerta"
                    type="time"
                    value={formData.hora_alerta}
                    onChange={(e) => setFormData({ ...formData, hora_alerta: e.target.value })}
                    data-testid="anotacao-hora-input"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="prioridade">Prioridade</Label>
                <Select
                  value={formData.prioridade}
                  onValueChange={(value) => setFormData({ ...formData, prioridade: value })}
                >
                  <SelectTrigger data-testid="anotacao-prioridade-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Alta">Alta</SelectItem>
                    <SelectItem value="Normal">Normal</SelectItem>
                    <SelectItem value="Baixa">Baixa</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={handleCloseDialog}>
                  Cancelar
                </Button>
                <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="save-anotacao-button">
                  {editingAnotacao ? 'Atualizar' : 'Criar'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-3 w-5 h-5 text-slate-400" />
          <Input
            placeholder="Buscar anotações..."
            className="pl-10"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            data-testid="search-anotacoes-input"
          />
        </div>
        <div className="w-48">
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger data-testid="anotacao-filter-select">
              <SelectValue placeholder="Filtrar" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todas">Todas</SelectItem>
              <SelectItem value="pendentes">Pendentes</SelectItem>
              <SelectItem value="concluidas">Concluídas</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-4">
        {filteredAnotacoes.length > 0 ? (
          filteredAnotacoes.map((anotacao) => {
            const dataAlerta = new Date(anotacao.data_alerta);
            const atrasada = isAtrasada(anotacao.data_alerta, anotacao.concluida);
            const hoje = isHoje(anotacao.data_alerta);
            
            return (
              <Card 
                key={anotacao.id} 
                className={`hover:shadow-lg transition-shadow ${anotacao.concluida ? 'opacity-60' : ''} ${atrasada ? 'border-red-300 bg-red-50' : hoje ? 'border-amber-300 bg-amber-50' : ''}`}
                data-testid="anotacao-card"
              >
                <CardContent className="p-4">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Bell className={`w-5 h-5 ${atrasada ? 'text-red-600' : hoje ? 'text-amber-600' : 'text-slate-600'}`} />
                        <h3 className={`font-semibold text-lg ${anotacao.concluida ? 'line-through text-slate-500' : 'text-slate-900'}`}>
                          {anotacao.titulo}
                        </h3>
                        <span className={`text-xs px-2 py-1 rounded border ${getPrioridadeColor(anotacao.prioridade)}`}>
                          {anotacao.prioridade}
                        </span>
                        {hoje && !anotacao.concluida && (
                          <span className="text-xs px-2 py-1 bg-amber-200 text-amber-800 rounded font-bold">HOJE</span>
                        )}
                        {atrasada && (
                          <span className="text-xs px-2 py-1 bg-red-200 text-red-800 rounded font-bold">ATRASADA</span>
                        )}
                        {anotacao.concluida && (
                          <span className="text-xs px-2 py-1 bg-green-200 text-green-800 rounded">Concluída</span>
                        )}
                      </div>
                      {anotacao.descricao && (
                        <p className="text-slate-600 mb-2">{anotacao.descricao}</p>
                      )}
                      <div className="flex items-center gap-4 text-sm text-slate-500">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {dataAlerta.toLocaleDateString('pt-BR')}
                        </div>
                        <div className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {dataAlerta.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {!anotacao.concluida && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-green-600 hover:text-green-700 hover:bg-green-50"
                          onClick={() => handleConcluir(anotacao.id)}
                          data-testid="concluir-anotacao-button"
                        >
                          <CheckCircle className="w-4 h-4" />
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(anotacao)}
                        data-testid="edit-anotacao-button"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(anotacao.id)}
                        data-testid="delete-anotacao-button"
                      >
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        ) : (
          <div className="text-center py-12 text-slate-500">
            Nenhuma anotação encontrada
          </div>
        )}
      </div>
    </div>
  );
};

export default Anotacoes;
